/**
 * Vera Ideas — notification bell for the AppShell header. Shows the unread
 * count; opening the popover lists recent notifications and marks them read.
 */

import { useNavigate } from 'react-router-dom';
import {
  Body1,
  Button,
  Caption1,
  CounterBadge,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { AlertRegular } from '@fluentui/react-icons';
import { useMarkNotificationsRead, useNotifications } from '@ideas/api/useIdeas';
import { notificationText, timeAgo } from '@ideas/lib/ideasUi';
import { useIdeasBasePath } from '@ideas/app/routes/routeTree';

const useStyles = makeStyles({
  bellWrap: { position: 'relative', display: 'inline-flex' },
  counter: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    pointerEvents: 'none',
  },
  surface: {
    maxWidth: '360px',
    minWidth: '300px',
    maxHeight: '420px',
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  unread: { backgroundColor: tokens.colorBrandBackground2 },
  empty: { padding: tokens.spacingVerticalL, textAlign: 'center', color: tokens.colorNeutralForeground3 },
});

export function NotificationsBell() {
  const styles = useStyles();
  const navigate = useNavigate();
  const basePath = useIdeasBasePath();
  const { data } = useNotifications();
  const markRead = useMarkNotificationsRead();

  const unread = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  return (
    <Popover
      positioning="below-end"
      onOpenChange={(_, d) => {
        if (d.open && unread > 0) markRead.mutate();
      }}
    >
      <PopoverTrigger disableButtonEnhancement>
        <span className={styles.bellWrap}>
          <Button
            appearance="subtle"
            icon={<AlertRegular />}
            aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
          />
          {unread > 0 && <CounterBadge count={unread} size="small" className={styles.counter} />}
        </span>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        {notifications.length === 0 ? (
          <div className={styles.empty}>
            <Body1>Nothing yet — submit or vote on an idea and we'll keep you posted! 💡</Body1>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              className={mergeClasses(styles.item, !n.read_at && styles.unread)}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`${basePath}/${n.idea_id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate(`${basePath}/${n.idea_id}`);
              }}
            >
              <Body1>{notificationText(n.kind, n.payload)}</Body1>
              <Caption1>{timeAgo(n.created_at)}</Caption1>
            </div>
          ))
        )}
      </PopoverSurface>
    </Popover>
  );
}
